import { useParams, Navigate, Link } from "react-router-dom";
import { MapPin, ShieldCheck, ChevronRight, ArrowRight } from "lucide-react";
import { Seo, PHONE, EMAIL } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { serviceBySlug, SERVICES } from "../data/services";
import { cityBySlug, cityPath, comboPath, CITIES } from "../data/cities";

export default function ComboPage() {
  const { citySlug, serviceSlug } = useParams();
  const city = cityBySlug(citySlug);
  const service = serviceBySlug(serviceSlug);
  if (!city || !service) return <Navigate to="/" replace />;

  const faqs = [
    {
      q: `How do I hire ${service.name.toLowerCase()} in ${city.name}?`,
      a: `Call our 24/7 dispatch at +1 807-770-0243 or email admin@1stnation-security.com with your ${city.name} location, site type and coverage hours. Our team will prepare a security plan and quote for your ${service.name.toLowerCase()} needs.`,
    },
    {
      q: `Is ${service.name.toLowerCase()} available 24/7 in ${city.name}?`,
      a: `Yes. 1st Nations Security provides ${service.name.toLowerCase()} in ${city.name} and across ${city.region} around the clock — days, nights, weekends and holidays — with licensed Ontario security guards.`,
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const sameServiceCities = CITIES.filter((c) => c.slug !== city.slug).slice(0, 8);
  const otherServicesInCity = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <div data-testid={`combo-page-${city.slug}-${service.slug}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title={`${service.name} ${city.name} | 1st Nations Security`}
        description={`${service.name} in ${city.name}, Ontario. Licensed security guards, 24/7 dispatch. Serving ${city.name} & ${city.region}. Call 1st Nations Security: +1 807-770-0243.`}
        path={comboPath(city, service)}
        jsonLd={jsonLd}
      />
      <nav data-testid="combo-breadcrumb" className="text-xs text-slate-500 mb-6 flex flex-wrap items-center gap-1.5">
        <Link to="/" className="hover:text-amber-300">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={cityPath(city)} className="hover:text-amber-300">Security Guards {city.name}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-300">{service.name}</span>
      </nav>

      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3 flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5" /> {city.name}, Ontario — {city.region}
      </p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        {service.name} in {city.name}, Ontario
      </h1>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">
        Looking for {service.name.toLowerCase()} in {city.name}? 1st Nations Security — a 100% Indigenous-owned
        Ontario security company — provides professional, licensed {service.name.toLowerCase()} throughout {city.name}
        {" "}and {city.region}. {service.short}
      </p>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">
        {service.intro}
      </p>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-10">
        In {city.name}, our {service.name.toLowerCase()} teams support key local sectors — including{" "}
        {city.industries.slice(0, 3).join(", ").toLowerCase()} and more. Every assignment is backed by licensed
        Ontario guards, detailed digital reporting and our 24/7 dispatch centre at {PHONE}.
      </p>

      <section data-testid="combo-included-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          {service.name} in {city.name} — What's Included
        </h2>
        <div className="grid sm:grid-cols-2 gap-4 max-w-4xl">
          {service.points.map((pt) => (
            <div key={pt} className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-900/60 px-5 py-4">
              <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-200">{pt}</span>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="combo-industries-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          {city.name} Industries We Serve
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          {city.industries.map((ind) => (
            <div key={ind} className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-900/60 px-5 py-4">
              <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-200">{ind} in {city.name}</span>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="combo-other-services-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          Other Security Services in {city.name}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {otherServicesInCity.map((s) => (
            <Link
              key={s.slug}
              to={comboPath(city, s)}
              data-testid={`combo-other-service-${s.slug}`}
              className="rounded-lg border border-white/10 bg-slate-950/70 px-3.5 py-2.5 text-sm text-slate-300 transition-colors hover:border-amber-500/40 hover:text-amber-300"
            >
              {s.name} in {city.name}
            </Link>
          ))}
        </div>
      </section>

      <section data-testid="combo-other-cities-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          {service.name} in Other Ontario Cities
        </h2>
        <div className="flex flex-wrap gap-3">
          {sameServiceCities.map((c) => (
            <Link
              key={c.slug}
              to={comboPath(c, service)}
              data-testid={`combo-other-city-${c.slug}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-amber-500/40 hover:text-amber-300"
            >
              <MapPin className="h-3.5 w-3.5 text-amber-500" /> {service.name} {c.name}
            </Link>
          ))}
          <Link
            to={`/security-services/${service.slug}`}
            data-testid="combo-service-overview-link"
            className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-4 py-2 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10"
          >
            All {service.name} Locations <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      <section data-testid="combo-faq-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          {service.name} in {city.name} — FAQs
        </h2>
        <div className="space-y-4 max-w-3xl">
          {faqs.map((f, i) => (
            <details key={i} data-testid={`combo-faq-${i}`} className="group rounded-xl border border-white/10 bg-slate-900/60 p-5">
              <summary className="cursor-pointer text-base font-semibold text-white list-none flex items-center justify-between gap-3">
                {f.q}
                <ChevronRight className="h-4 w-4 text-amber-400 shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <CtaBand
        heading={`Get ${service.name} in ${city.name} Today`}
        sub={`Call our 24/7 dispatch at ${PHONE} or email ${EMAIL} for ${service.name.toLowerCase()} in ${city.name}, Ontario — licensed guards, fast deployment, professional reporting.`}
      />
    </div>
  );
}
